import { CheckCircle2, XCircle, Clock, Trophy } from 'lucide-react';
import { useGetUserSelectionsQuery } from '@/api/pickems/getPickemEntries';

interface SelectionsDetailProps {
  pickemId: string;
  userId: string;
  username: string;
}

export function SelectionsDetail({ pickemId, userId, username }: SelectionsDetailProps) {
  const { data, isLoading, error } = useGetUserSelectionsQuery(pickemId, userId);

  if (isLoading) {
    return <div className="p-4 text-center text-sm text-gray-500">Loading selections...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-sm text-red-500">Failed to load selections</div>;
  }

  if (!data || data.selections.length === 0) {
    return <div className="p-4 text-center text-sm text-gray-500">No selections found</div>;
  }

  const totalPoints = data.selections.reduce((sum, sel) => sum + sel.pointsEarned, 0);

  return (
    <div className="bg-gray-50 p-4 border-t">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-gray-900">{username}'s Selections</h4>
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-600" />
          <span className="text-sm font-medium">{totalPoints} Total Points</span>
        </div>
      </div>

      <div className="space-y-3">
        {data.selections.map((selection, index) => {
          const isCorrect = selection.isCorrect === true;
          const isIncorrect = selection.isCorrect === false;
          const isPending = selection.isCorrect === null;

          return (
            <div
              key={selection.questionId}
              className="bg-white rounded-lg p-3 border border-gray-200"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {isCorrect && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                  {isIncorrect && <XCircle className="w-5 h-5 text-red-600" />}
                  {isPending && <Clock className="w-5 h-5 text-gray-400" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500">Q{index + 1}</span>
                  </div>

                  <p className="text-sm font-medium text-gray-900 mb-1">{selection.questionText}</p>

                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      → <span className="font-medium">{selection.optionText}</span>
                    </p>

                    <div className="flex items-center gap-2">
                      {selection.pointsEarned > 0 && (
                        <span className="text-sm font-semibold text-green-600">
                          +{selection.pointsEarned} pts
                        </span>
                      )}
                      {isIncorrect && <span className="text-sm text-gray-500">0 pts</span>}
                      {isPending && <span className="text-xs text-gray-400">Pending</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
